'use client';

import { useParams } from 'next/navigation';
import { useDefaultLocale, useSupportedLocales } from '@/providers/root-provider';
import { getLocaleCookie } from '@/utils/locale-cookie';

/**
 * Absolute last resort, only reachable when the tenant's CORE config is unavailable
 * (no supported languages, no default language). The storefront has no English
 * content, so `hu` is the safe floor rather than `en`.
 */
export const FALLBACK_LOCALE = 'hu';

/**
 * Reads the browser's preferred languages and returns the first one the storefront
 * supports. `navigator.languages` entries are tags like `hu-HU`, so we compare on the
 * primary subtag as well.
 */
const resolveBrowserLocale = (supported: string[] | undefined): string | undefined => {
  if (typeof navigator === 'undefined') return undefined;
  if (!supported?.length) return undefined;

  const supportedSet = new Set(supported.map(code => code.trim().toLowerCase()));
  const candidates = navigator.languages?.length
    ? navigator.languages
    : [navigator.language].filter(Boolean);

  for (const tag of candidates) {
    const lower = tag.trim().toLowerCase();
    if (supportedSet.has(lower)) return lower;
    const primary = lower.split('-')[0];
    if (primary && supportedSet.has(primary)) return primary;
  }

  return undefined;
};

/**
 * Resolves the locale to use for client-side URL building.
 *
 * Needed because not every route has a `[locale]` segment — the root `/` landing page
 * (`src/app/page.tsx`) renders the default-locale storefront without one, so
 * `useParams().locale` is `undefined` there. Interpolating that straight into a URL
 * produced links like `/undefined`.
 *
 * Order: locale cookie -> `[locale]` route param -> tenant default language ->
 * browser language (only when the storefront supports it) -> first supported language.
 */
export const useLocale = (): string => {
  const params = useParams();
  const defaultLocale = useDefaultLocale();
  const supportedLocales = useSupportedLocales();

  const supportedSet = supportedLocales?.length
    ? new Set(supportedLocales.map(code => code.trim().toLowerCase()))
    : undefined;
  // Only trust a cookie the storefront still supports — a stale code (e.g. a language
  // removed in Studio) would otherwise build URLs that 404.
  const isSupported = (code: string | undefined) =>
    Boolean(code) && (!supportedSet || supportedSet.has(code as string));

  const rawCookieLocale = getLocaleCookie()?.trim().toLowerCase();
  const cookieLocale = isSupported(rawCookieLocale) ? rawCookieLocale : undefined;
  const paramLocale = typeof params.locale === 'string' ? params.locale.toLowerCase() : undefined;
  const firstSupportedLocale = supportedLocales?.[0]?.trim().toLowerCase() || undefined;

  return (
    cookieLocale ??
    paramLocale ??
    defaultLocale?.toLowerCase() ??
    resolveBrowserLocale(supportedLocales) ??
    firstSupportedLocale ??
    FALLBACK_LOCALE
  );
};

export default useLocale;
